// @ts-check
import {
  computeEdgesAfterUngroup,
  computeEdgesWithNewGroup,
  computeNodesAfterUngroup,
  computeNodesWithNewGroup,
  prepareGroups,
} from './groupUtils';
import { getSaveableTracerObject } from './saveTracer';
import {
  computeEdgeStateWithNewTokenFlow,
  computeNodeStateWithNewTokenFlow,
  computeNodeStateWithNewTransaction,
  prepareFocusNode,
  removeQueryFromEdge,
  removeQueryFromNode,
} from './tracerStoreUtils';
import { applyEdgeChanges, applyNodeChanges } from '@xyflow/react';
import moment from 'moment';
import { create } from 'zustand';

import { queryClient } from 'stateManagement/queries/client';
import {
  tracerTokenFlowQueryKey,
  tracerTransactionQueryKey,
} from 'stateManagement/queries/services/tracer/tracerQueries';
import {
  createTracer_Mutation,
  updateTracer_Mutation,
} from 'stateManagement/queries/services/tracer/utils';

import { CHAINS } from 'blockscope/static/supportedChains';

import {
  addressNodeId,
  colorAttachedEdges,
  colorSingleEdge,
  dagrify,
  newGenericEdge,
  newGenericNode,
  newGroupEdge,
  newGroupNode,
  newQueryId,
  newTextEdge,
  newTextNode,
  tokenFlowEdgeId,
} from 'blockscope/components/tracer/utils/reactFlowUtils';
import { extractAddress } from 'blockscope/utils/helperUtils';

/**
 * @typedef {import('blockscope/static/chainData').enrichedBalance} EnrichedBalance
 * @typedef {import('blockscope/static/chainData').tokenData} TokenData
 * @typedef {import('blockscope/static/chainData').enrichedAddress} EnrichedAddress
 * @typedef {import('stateManagement/queries/services/tracer/zod').TransactionFlowResults} TransactionFlowResults
 * @typedef {import('stateManagement/queries/services/tracer/zod').TokenFlowResults} TokenFlowResults
 * @typedef {import('stateManagement/queries/services/tracer/zod').TracerSettings} TracerSettings
 */

/**
 * @typedef {Object} WalletSearchParams
 * @property {String[]} filterAddress
 * @property {String} chain
 * @property {String} startTime
 * @property {String} endTime
 * @property {Array<String>} tokens
 */

/**
 * @typedef {Object} TransactionSearchParams
 * @property {String[]} transactions
 * @property {String} chain
 */

/**
 * @typedef {Object} SingleQuerySpec - The query specification
 * @property {String} tokenAddress - The token address
 * @property {String} filterAddress - The filter address, the address that the user is queried for
 * @property {String} walletAddress - The wallet address to add
 * @property {EnrichedBalance} flow - The amount
 * @property {String} source - the source address of the fund transfer
 * @property {Number} target - the target address of the fund transfer
 * @property {String} chain - The chain of the fund transfer
 * @property {String} startTime - The start time of the query YYYY-MM-DD
 * @property {String} endTime - The end time of the query YYYY-MM-DD
 * @property {String} [transactionHash] - The transactionhash of the query
 */

/**
 * Used to add nodes from the query table on the bottom of the screen. Used when address is not in graph.
 * @typedef {Object} SingleNodeSpec - The node specification
 * @property {EnrichedAddress} walletAddress - The wallet address to add
 * @property {String} chain - The chain of the wallet address
 */

/**
 * @typedef {Object} GroupingSpec - The grouping specification
 * @property {String[]} nodeIds - The nodeIds to add to the group
 * @property {String} group - The group to add the nodes to. Either a groupId or groupName (if the group is new)
 */

/**
 * @typedef {Object} InterestedAddressObject - The interested address object
 * @property {String} address - The address
 * @property {String} chain - The chain
 */

/**
 * @typedef {Object} NodeTokenFlowSearchParams - The node search params
 * @property {String} chain
 * @property {String} startTime
 * @property {String} endTime
 * @property {String | EnrichedAddress} filterAddress
 * @property {String | TokenData} tokenAddress
 */

/**
 * @typedef {Object} NodeTransactionSearchParams - The node search params
 * @property {String} chain
 * @property {String} transactionHash
 */

/**
 * @typedef {Object} AddressNodeData - The edge object
 * @property {string} chain
 * @property { EnrichedAddress} address
 * @property {boolean} interested
 * @property {(NodeTransactionSearchParams | NodeTokenFlowSearchParams)[]} searchParams
 */

/**
 * @typedef {Object} AddressNode - The node object
 * @property {String} id - The id of the node
 * @property {'addressNode'} type - The type of the node
 * @property {{x: number, y: number}} position
 * @property {{x: number, y: number}} [savedPosition]
 * @property {AddressNodeData} data
 */

/**
 @typedef {Object} TextNode - The node object
 * @property {String} id - The id of the node
 * @property {'textNode'} type - The type of the node
 * @property {{x: number, y: number}} position
 * @property {{x: number, y: number}} [savedPosition]
 * @property {{width: number, height: number}} [savedSize]
 * @property {Number} width
 * @property {Number} height
 * @property {{text:string}} data
 */

/**
 * @typedef {Object} GroupNode - The node object
 * @property {String} id - The id of the node
 * @property {'addressGroup'} type - The type of the node
 * @property {{x: number, y: number}} position
 * @property {{x: number, y: number}} [savedPosition]
 * @property {GroupNodeData} data
 */

/**
 * @typedef {AddressNode | GroupNode | TextNode} TracerNode
 */

/**
 * @typedef {TracerNode & {nodeId: string}} SavedNode
 */

/**
 * @typedef {TracerEdge & {edgeId: string}} SavedEdge
 */

/**
 * @typedef {Object} tokenFlowEdgeQuery - The node search params
 * @property {String} chain
 * @property {String} startTime
 * @property {String} endTime
 * @property {String | EnrichedAddress} sourceAddress
 * @property {String | EnrichedAddress} targetAddress
 * @property {String | TokenData} token
 * @property {'erc20' | 'native'} tokenType
 * @property {EnrichedBalance} amount
 */

/**
 * @typedef {Object} transactionEdgeQuery - The node search params
 * @property {String} chain
 * @property {String} txnTime
 * @property {String} transactionHash
 * @property {String | EnrichedAddress} sourceAddress
 * @property {String | EnrichedAddress} targetAddress
 * @property {String | TokenData} token
 * @property {EnrichedBalance} amount
 */

/**
 *  @typedef {Object} GenericEdgeData
 *  @property {Record<string, (tokenFlowEdgeQuery | transactionEdgeQuery)>} queries
 *  @property {string} source
 *  @property {string} target
 */

/**
 * @typedef {Object} GenericEdge - The edge object
 * @property {String} id - The id of the edge
 * @property {String} source - The source of the edge
 * @property {String} target - The target of the edge
 * @property {'genericEdge'} type - The type of the edge
 * @property {GenericEdgeData} data
 */

/**
 * @typedef {Object} GroupEdge
 * @property {String} id - The id of the edge
 * @property {String} source - The source of the edge
 * @property {String} target - The target of the edge
 * @property {'groupEdge'} type - The type of the edge
 * @property {{source: string, target: string, members: GenericEdge[]}} data
 */

/**
 * @typedef {Object} TextEdge
 * @property {String} id - The id of the edge
 * @property {String} source - The source of the edge
 * @property {String} target - The target of the edge
 * @property {'textEdge'} type - The type of the edge
 * @property {{}} data
 */

/**
 * @typedef {GroupEdge | GenericEdge | TextEdge} TracerEdge
 */

/**
 * @typedef {Object} GroupNodeData - The edge object
 * @property {AddressNode[]} members
 * @property {string} name
 * @property {{x: number, y: number}} position
 * @property {{x: number, y: number}} [savedPosition]
 * @property {GenericEdge[]} internalEdges
 */

/**
 * @typedef {Object} InteractionTableTransferSpec
 * @property {EnrichedAddress} focusAddress
 * @property {EnrichedAddress} source
 * @property {EnrichedAddress} target
 * @property {String} chain
 * @property {EnrichedBalance} value
 * @property {TokenData} token
 * @property {String} startTime
 * @property {String} endTime
 */

/**
 * @typedef {Object} SingleTransactionTransferSpec
 * @property {TokenData} tokenAddress
 * @property {EnrichedAddress} sourceAddress
 * @property {EnrichedAddress} targetAddress
 * @property {EnrichedAddress} focusAddress
 * @property {EnrichedBalance} flow
 * @property {String} chain
 * @property {String} transactionHash
 * @property {Number} txnTime
 */

/**
 * @typedef {Object} RawQueryRow
 * @property {TokenData} tokenAddress
 * @property {EnrichedAddress} sourceAddress
 * @property {EnrichedAddress} targetAddress
 * @property {EnrichedAddress} [filterAddress]
 * @property {EnrichedBalance} amount
 * @property {String} chain
 * @property {String} [transactionHash]
 * @property {String} [startTime]
 * @property {String} [endTime]
 * @property {Number} [txnTime]
 */

/**
 * @typedef {Object} TracerStore - The store for the Tracer component
 * @property {String | null} currentTracerId
 * @property {String | null} currentTracerName
 * @property {TracerNode[]} nodes - The nodes in the graph
 * @property {TracerEdge[]} edges - The edges in the graph
 * @property {GroupNode[]} groups - The groups in the graph
 * @property {RawQueryRow[]} rawQueryData - The raw query data from the API
 * @property {Record<string, Map<string, string>>} addressMap - The address map
 *
 * @property {AddressNodeData | GroupNodeData | {}} selectedNodeData - The data of the selected node
 * @property {boolean} unsavedChanges - Whether there are unsaved changes
 * @property {InterestedAddressObject[]} interestedAddresses - The interested addresses
 * @property {NodeTokenFlowSearchParams[]} recentlyAddedData - The recently added data
 * @property {String | null} nodeIdToZoom - The nodeId to zoom
 * @property {boolean} autoLayout - autoLayout Flag
 * @property {boolean} showUniqueTokens - showUniqueTokens Flag
 * @property {boolean} showTimeRange - showTimeRange Flag
 * @property {boolean} showEdgeValue - showEdgeValue Flag
 * @property {boolean} showChain - showChain Flag
 * @property {boolean} showHoldingValue - showHoldingValue Flag
 * @property {boolean} showMiniMap - showMiniMap Flag
 * @property {boolean} showAddressRisk - showAddressRisk Flag
 *
 * React Flow Functions
 * @property {(changes: Object) => void} onNodesChange - Changes the nodes
 * @property {(changes: Object) => void} onEdgesChange - Changes the edges
 * @property {(data: Object) => void} onConnect
 * @property {(node: Object) => void} onNodeClick - Handles node click
 *
 * Our Functions
 * @property {(autoLayout: boolean) => void} updateAutoLayout - Update autoLayout flag
 * @property {(data: NodeTokenFlowSearchParams) => void} onAddData - Adds data
 * @property {(address: String, chain: String) => boolean} nodeInGraph - Checks if a node is in the graph
 * @property {(address: String | EnrichedAddress, chain: String) => boolean} addressInGraph - Checks if an address is in the graph
 * @property {(arg0: SingleQuerySpec) => boolean} queryInGraph - Checks if a query is in the graph
 * @property {(search: WalletSearchParams, setSearchParams: import('react-router-dom').SetURLSearchParams) => void} addNewFlowSection - Adds a new flow section
 * @property {(search: TransactionSearchParams, setSearchParams: import('react-router-dom').SetURLSearchParams) => void} addNewTransactions - Adds new transactions
 * @property {(arg0: SingleNodeSpec) => void} addSingleNode - Adds a single node
 * @property {(arg0: SingleQuerySpec) => void} addSingleQuery - Adds a single query
 * @property {(arg0: SingleTransactionTransferSpec) => void} addSingleTransactionTransfer - Adds a single transaction transfer
 * @property {(arg0: InteractionTableTransferSpec) => void} addTransferFromInteractionTable - Adds a transfer from the interaction table
 * @property {(nodeId: String) => void} removeNode - Removes a node
 * @property {(edgeId: String, queryKey: String, removeFloatingNodes?: boolean) => void} removeQueryFromEdge - Removes a query from an edge
 * @property {() => void} clearTracer - Clears the tracer
 * @property {() => void} updateInterestedNodes - Updates the interested nodes
 * @property {() => void} resetSpacing - Resets the spacing
 * @property {(savedNodes: SavedNode[], savedEdges: SavedEdge[], savedSettings: TracerSettings) => void} loadSavedTracer - Loads a saved tracer
 * @property {(unsavedChanges: boolean) => void} setUnsavedChanges - Sets the unsaved changes tracker
 * @property {(interestedAddresses: InterestedAddressObject[]) => void} setInterestedAddresses - Sets the interested addresses
 * @property {(arg0: GroupingSpec) => void} addToGroup - Adds a set of nodes to a group
 * @property {(groupId: String) => void} ungroupNodes - Ungroups nodes
 * @property {(data: TokenFlowResults, search: WalletSearchParams) => void} populateTokenFlowQueryData - Populates the query data
 * @property {(data: TransactionFlowResults, search: {chain: string, transactionHash: string}) => void} populateTransactionFlowQueryData - Populates the query data
 * @property {(nodeId: string, queryKey: string, removeFloatingNodes?: boolean) => void} removeQueryFromNode - Removes a query from a node
 * @property {(text: string, position: {x: number, y: number}) => void} addTextNode - Adds a text node
 * @property {(nodeId: string, text: string) => void} updateTextNode - Updates a text node
 * @property {(name: string) => void} updateTracerName - Updates the tracer name
 * @property {(id: string) => void} updateTracerId - Updates the tracer description
 * @property {(flag: boolean)=>void} updateShowUniqueTokens - showUniqueTokens Flag
 * @property {(flag: boolean)=>void} updateShowTimeRange - showTimeRange Flag
 * @property {(flag: boolean)=>void} updateShowEdgeValue - showEdgeValue Flag
 * @property {(flag: boolean)=>void} updateShowChain - showChain Flag
 * @property {(flag: boolean)=>void} updateShowUniqueTokens - showUniqueTokens Flag
 * @property {(flag: boolean)=>void} updateShowHoldingValue - showHoldingValue Flag
 * @property {(flag: boolean)=>void} updateShowMiniMap - showMiniMap Flag
 * @property {(flag: boolean)=>void} updateShowAddressRisk - showAddressRisk Flag
 */

const initState = {
  currentTracerId: null,
  currentTracerName: null,

  // UI Components that get displayed
  nodes: [],
  edges: [],

  // duplicate subset of group nodes --> used to keep track of groups for convenience (not displayed on the graph)
  groups: [],

  rawQueryData: [], // raw query data from the API contains addresses and flows

  // address pointing to { address -> nodeId | groupId }
  addressMap: {
    [CHAINS.ETHEREUM]: new Map(),
    [CHAINS.POLYGON]: new Map(),
    [CHAINS.FANTOM]: new Map(),
    [CHAINS.ARBITRUM]: new Map(),
    [CHAINS.OPTIMISM]: new Map(),
    [CHAINS.TRON]: new Map(),
    [CHAINS.BASE]: new Map(),
    [CHAINS.AVALANCHE_C]: new Map(),
  },

  selectedNodeData: {},
  unsavedChanges: false,
  interestedAddresses: [],
  recentlyAddedData: [],
  nodeIdToZoom: null,
  autoLayout: true,

  showLabels: true,
  showEdgeValue: true,
  showTimeRange: false,
  showMiniMap: true,
  showChain: true,
  showUniqueTokens: true,
  showHoldingValue: true,
  showAddressRisk: true,
};

/**
 * Store for the Tracer
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<TracerStore>>}
 * */
export const useTracerStore = create((set, get) => ({
  ...initState,

  updateTracerId(id) {
    set(() => ({ currentTracerId: id }));
  },
  updateTracerName(name) {
    set(() => ({ currentTracerName: name }));
  },

  updateAutoLayout(autoLayout) {
    set(() => ({ autoLayout }));
  },
  updateShowMiniMap(showMiniMap) {
    set(() => ({ showMiniMap }));
  },
  updateShowTimeRange(showTimeRange) {
    set(() => ({ showTimeRange }));
  },
  updateShowEdgeValue(showEdgeValue) {
    set(() => ({ showEdgeValue }));
  },
  updateShowChain(showChain) {
    set(() => ({ showChain }));
  },
  updateShowUniqueTokens(showUniqueTokens) {
    set(() => ({ showUniqueTokens }));
  },
  updateShowHoldingValue(showHoldingValue) {
    set(() => ({ showHoldingValue }));
  },
  updateShowAddressRisk(showAddressRisk) {
    set(() => ({ showAddressRisk }));
  },

  onAddData(data) {
    let newRecentlyAddedData = [...get().recentlyAddedData];

    if (
      newRecentlyAddedData.filter((dataEntry) => {
        return (
          dataEntry.startTime === data.startTime &&
          dataEntry.endTime === data.endTime &&
          dataEntry.tokenAddress === data.tokenAddress &&
          dataEntry.chain === data.chain
        );
      }).length === 0
    ) {
      newRecentlyAddedData.push(data);
    }
    set({
      recentlyAddedData: newRecentlyAddedData,
    });
  },

  onNodesChange(changes) {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },

  onEdgesChange(changes) {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },

  onConnect(data) {
    const edge = data.source.includes('text:::')
      ? newTextEdge(data)
      : { ...data };

    set({ edges: [edge, ...get().edges] });
  },

  nodeInGraph(address, chain) {
    for (const node of get().nodes) {
      if (
        node.type === 'addressNode' &&
        extractAddress(node.data.address) === extractAddress(address) &&
        node.data.chain === chain
      ) {
        return true;
      }
    }
    return false;
  },

  nodeInGroup(address, chain) {
    for (const group of get().groups) {
      for (const member of group.data.members) {
        if (
          extractAddress(member.data.address) === extractAddress(address) &&
          member.data.chain === chain
        ) {
          return group;
        }
      }
    }
    return false;
  },

  addressInGraph(address, chain) {
    return get().addressMap[chain]?.has(extractAddress(address));
  },

  queryInGraph({
    source,
    target,
    tokenAddress,
    filterAddress,
    transactionHash,
    chain,
    startTime,
    endTime,
  }) {
    const edgeIdToFind = tokenFlowEdgeId({
      source,
      target,
    });
    const queryToFind = newQueryId({
      tokenAddress,
      filterAddress,
      chain,
      start: startTime,
      end: endTime,
      transactionHash,
    });

    for (const edge of get().edges) {
      if (edge.id === edgeIdToFind) {
        if (edge.type == 'genericEdge' && edge.data.queries) {
          return queryToFind in edge.data.queries;
        } else if (edge.type == 'groupEdge' && edge.data.members) {
          for (const member of edge.data.members) {
            if (queryToFind in member.data.queries) {
              return true;
            }
          }
        }
        return false;
      }
    }
    return false;
  },

  addNewFlowSection(search, setSearchParams) {
    const {
      nodes,
      edges,
      rawQueryData,
      interestedAddresses,
      addressMap,
      currentTracerId,
      currentTracerName,
      autoLayout,
      showUniqueTokens,
      showTimeRange,
      showEdgeValue,
      showHoldingValue,
      showChain,
      showMiniMap,
      showAddressRisk,
    } = get();

    // all variables that are going to be modified
    let newNodesArr = [...nodes];
    const newEdgesArr = [...edges];
    let newQueryData = [...rawQueryData];
    const newAddressMap = new Map(addressMap[search.chain]);

    // takes search from modal form and creates queries for backend
    const searchParams = [];
    search.filterAddress.forEach((addr) => {
      search.tokens.forEach((token) => {
        searchParams.push({
          // TODO: Viral check if token is a string or object
          ...token,
          filterAddress: addr,
          chain: search.chain,
          startTime: search.startTime,
          endTime: search.endTime,
        });
      });
    });

    let focusNode;
    for (const searchParam of searchParams) {
      /** @type {TokenFlowResults | undefined} */
      const queryData = queryClient.getQueryData(
        tracerTokenFlowQueryKey(searchParam)
      );

      if (queryData) {
        const { results, params } = queryData;

        focusNode = prepareFocusNode({
          addressMap: newAddressMap,
          filterAddress: params.filterAddress,
          chain: search.chain,
          nodes: newNodesArr,
          searchParams: [params],
        });

        if (!focusNode.position) {
          focusNode.position = { x: 0, y: 0 };
        }

        computeNodeStateWithNewTokenFlow({
          focusNode,
          nodes: newNodesArr,
          rawQueryData: newQueryData,
          interestedAddresses,
          addressMap: newAddressMap,
          results,
          params,
        });
      }
    }

    if (focusNode === undefined) {
      throw new Error(
        'Unreachable state. Node should always be defined by this point'
      );
    }

    // recalculating the edges
    computeEdgeStateWithNewTokenFlow({
      edges: newEdgesArr,
      addressMap: newAddressMap,
      rawQueryData: newQueryData,
    });
    const dagrified = dagrify(newNodesArr, newEdgesArr, autoLayout);
    // Set the new state
    set({
      nodes: autoLayout ? dagrified.nodes : newNodesArr,
      edges: colorAttachedEdges(newEdgesArr, search.filterAddress),
      rawQueryData: newQueryData,
      addressMap: {
        ...addressMap,
        [search.chain]: newAddressMap,
      },
      selectedNodeData: {
        ...focusNode.data,
      },
      nodeIdToZoom: focusNode.id,
      unsavedChanges: true,
    });
    if (newNodesArr.length > 0) {
      if (currentTracerId) {
        updateTracer_Mutation(
          getSaveableTracerObject(
            autoLayout ? dagrified.nodes : newNodesArr,
            newEdgesArr,
            interestedAddresses,
            {
              autoLayout,
              showUniqueTokens,
              showTimeRange,
              showEdgeValue,
              showHoldingValue,
              showChain,
              showMiniMap,
              showAddressRisk,
            },
            currentTracerName,
            currentTracerId
          )
        );
      } else {
        createTracer_Mutation(
          getSaveableTracerObject(
            autoLayout ? dagrified.nodes : newNodesArr,
            newEdgesArr,
            interestedAddresses,
            {
              autoLayout,
              showUniqueTokens,
              showTimeRange,
              showEdgeValue,
              showHoldingValue,
              showChain,
              showMiniMap,
              showAddressRisk,
            },
            `Tracer ${moment().format('MMM D, YYYY, h:mm A')}`
          ),
          setSearchParams
        );
      }
    }
  },

  addNewTransactions(search, setSearchParams) {
    const {
      nodes,
      edges,
      rawQueryData,
      addressMap,
      currentTracerId,
      currentTracerName,
      interestedAddresses,
      autoLayout,
      showUniqueTokens,
      showTimeRange,
      showEdgeValue,
      showHoldingValue,
      showChain,
      showMiniMap,
      showAddressRisk,
    } = get();

    // all variables that are going to be modified
    let newNodesArr = [...nodes];
    const newEdgesArr = [...edges];
    let newQueryData = [...rawQueryData];
    const newAddressMap = new Map(addressMap[search.chain]);

    // takes search from modal form and creates queries for backend
    for (const txnHash of search.transactions) {
      /**
       * @type {TransactionFlowResults | undefined}
       */
      const queryData = queryClient.getQueryData(
        tracerTransactionQueryKey({
          chain: search.chain,
          transactionHash: txnHash,
        })
      );

      if (queryData) {
        const {
          params: { transactionHash },
          results: { tokenFlow, failed, txnTime },
        } = queryData;

        if (failed || tokenFlow.length === 0) {
          continue;
        }

        computeNodeStateWithNewTransaction({
          nodes: newNodesArr,
          rawQueryData: newQueryData,
          addressMap: newAddressMap,
          results: { txnTime, tokenFlow },
          params: { chain: search.chain, transactionHash },
        });

        // recalculating the edges
        computeEdgeStateWithNewTokenFlow({
          edges: newEdgesArr,
          addressMap: newAddressMap,
          rawQueryData: newQueryData,
        });
      }
    }

    // Re calculate the layout
    const dagrifiedGraph = dagrify(newNodesArr, newEdgesArr);

    // Set the new state
    set({
      nodes: dagrifiedGraph.nodes,
      edges: colorAttachedEdges(dagrifiedGraph.edges),
      rawQueryData: newQueryData,
      addressMap: {
        ...addressMap,
        [search.chain]: newAddressMap,
      },
      unsavedChanges: true,
    });

    if (newNodesArr.length > 0) {
      if (currentTracerId) {
        updateTracer_Mutation(
          getSaveableTracerObject(
            dagrifiedGraph.nodes,
            newEdgesArr,
            interestedAddresses,
            {
              autoLayout,
              showUniqueTokens,
              showTimeRange,
              showEdgeValue,
              showHoldingValue,
              showChain,
              showMiniMap,
              showAddressRisk,
            },
            currentTracerName,
            currentTracerId
          )
        );
      } else {
        createTracer_Mutation(
          getSaveableTracerObject(
            dagrifiedGraph.nodes,
            newEdgesArr,
            interestedAddresses,
            {
              autoLayout,
              showUniqueTokens,
              showTimeRange,
              showEdgeValue,
              showHoldingValue,
              showChain,
              showMiniMap,
              showAddressRisk,
            },
            `Tracer ${moment().format('MMM D, YYYY, h:mm A')}`
          ),
          setSearchParams
        );
      }
    }
  },

  addSingleNode({ walletAddress, chain }) {
    const {
      nodes,
      edges,
      interestedAddresses,
      rawQueryData,
      addressMap,
      currentTracerName,
      currentTracerId,
      autoLayout,
      showUniqueTokens,
      showTimeRange,
      showEdgeValue,
      showHoldingValue,
      showChain,
      showMiniMap,
      showAddressRisk,
    } = get();

    const newNodesArr = [...nodes];
    const newEdgesArr = [...edges];
    const newAddressMap = new Map(addressMap[chain]);

    const newNode = newGenericNode(
      {
        address: walletAddress,
        interested: interestedAddresses
          .filter((ia) => chain === ia.chain)
          .includes(extractAddress(walletAddress)),
        chain,
      },
      {
        x: 0,
        y: 0,
      }
    );

    newNodesArr.push(newNode);
    newAddressMap.set(extractAddress(walletAddress), newNode.id);

    // recalculating the edges
    for (let i = 0; i < rawQueryData.length; i++) {
      const {
        sourceAddress,
        targetAddress,
        filterAddress,
        tokenAddress,
        startTime,
        endTime,
        chain: dataRowChain,
      } = rawQueryData[i];

      // check if both source and target addresses are in the graph AND if source or target are the node we just added

      const sourceIsInGraph = newAddressMap.has(extractAddress(sourceAddress));
      const targetIsInGraph = newAddressMap.has(extractAddress(targetAddress));
      const sourceOrTargetIsAddedWallet =
        extractAddress(sourceAddress) === extractAddress(walletAddress) ||
        extractAddress(targetAddress) === extractAddress(walletAddress);

      if (sourceIsInGraph && targetIsInGraph && sourceOrTargetIsAddedWallet) {
        const source = newAddressMap.get(extractAddress(sourceAddress));
        const target = newAddressMap.get(extractAddress(targetAddress));

        const queryId = newQueryId({
          filterAddress,
          tokenAddress,
          start: startTime,
          end: endTime,
          chain: dataRowChain,
        });

        const tempEdge = newEdgesArr.find(
          (edge) => edge.id === tokenFlowEdgeId({ source, target })
        );

        if (tempEdge && 'queries' in tempEdge.data) {
          tempEdge.data.queries[queryId] = rawQueryData[i];
        } else {
          newEdgesArr.push(
            newGenericEdge({
              source,
              target,
              queries: { [queryId]: rawQueryData[i] },
            })
          );
        }
      }
    }

    set({
      nodes: newNodesArr,
      edges: colorAttachedEdges(newEdgesArr, newNode.id),
      addressMap: {
        ...addressMap,
        [chain]: newAddressMap,
      },
      unsavedChanges: true,
    });

    updateTracer_Mutation(
      getSaveableTracerObject(
        newNodesArr,
        newEdgesArr,
        interestedAddresses,
        {
          autoLayout,
          showUniqueTokens,
          showTimeRange,
          showEdgeValue,
          showHoldingValue,
          showChain,
          showMiniMap,
          showAddressRisk,
        },
        currentTracerName,
        currentTracerId
      )
    );
  },

  addSingleQuery({
    tokenAddress,
    filterAddress,
    walletAddress,
    flow,
    chain,
    startTime,
    endTime,
  }) {
    const { edges } = get();
    const newEdgesArr = [...edges];

    const queryId = newQueryId({
      tokenAddress,
      filterAddress,
      chain,
      start: startTime,
      end: endTime,
    });

    const isInflow = flow.displayText.charAt(0) !== '-';
    const sourceAddress = isInflow ? walletAddress : filterAddress;
    const targetAddress = isInflow ? filterAddress : walletAddress;
    const edgeId = tokenFlowEdgeId({
      source: addressNodeId({ chain, address: sourceAddress }),
      target: addressNodeId({ chain, address: targetAddress }),
    });

    const record = {
      amount: flow,
      tokenAddress,
      sourceAddress,
      targetAddress,
      filterAddress,
      chain,
      startTime,
      endTime,
    };

    const tempEdge = newEdgesArr.find((edge) => {
      return edge.id === edgeId;
    });

    if (tempEdge && tempEdge.type == 'genericEdge') {
      tempEdge.data.queries[queryId] = record;
    } else {
      newEdgesArr.push(
        newGenericEdge({
          source: addressNodeId({ chain, address: sourceAddress }),
          target: addressNodeId({ chain, address: targetAddress }),
          queries: { [queryId]: record },
        })
      );
    }
    set({
      edges: colorAttachedEdges(
        newEdgesArr,
        addressNodeId({ chain, address: filterAddress })
      ),
      unsavedChanges: true,
    });
  },

  addSingleTransactionTransfer({
    tokenAddress,
    sourceAddress,
    targetAddress,
    focusAddress,
    flow,
    chain,
    transactionHash,
    txnTime,
  }) {
    const { edges } = get();
    const newEdgesArr = [...edges];

    const queryId = newQueryId({
      tokenAddress,
      chain,
      transactionHash,
    });

    const edgeId = tokenFlowEdgeId({
      source: addressNodeId({ chain, address: sourceAddress }),
      target: addressNodeId({ chain, address: targetAddress }),
    });
    const record = {
      amount: flow,
      tokenAddress,
      sourceAddress,
      targetAddress,
      chain,
      transactionHash,
      txnTime,
    };

    const tempEdge = newEdgesArr.find((edge) => {
      return edge.id === edgeId;
    });

    if (tempEdge && tempEdge.type === 'genericEdge') {
      tempEdge.data.queries[queryId] = record;
    } else {
      newEdgesArr.push(
        newGenericEdge({
          source: addressNodeId({ chain, address: sourceAddress }),
          target: addressNodeId({ chain, address: targetAddress }),

          queries: { [queryId]: record },
        })
      );
    }
    set({
      edges: colorAttachedEdges(
        newEdgesArr,
        addressNodeId({ chain, address: extractAddress(focusAddress) })
      ),
      unsavedChanges: true,
    });
  },

  addTransferFromInteractionTable({
    focusAddress,
    source,
    target,
    chain,
    value,
    token,
    startTime,
    endTime,
  }) {
    const {
      nodes,
      edges,
      interestedAddresses,

      addressMap,
      addressInGraph,
      currentTracerName,
      currentTracerId,
      autoLayout,
      showUniqueTokens,
      showTimeRange,
      showEdgeValue,
      showHoldingValue,
      showChain,
      showMiniMap,
      showAddressRisk,
    } = get();

    const newNodesArr = [...nodes];
    const newEdgesArr = [...edges];
    const newAddressMap = new Map(addressMap[chain]);
    if (!addressInGraph(source, chain)) {
      const newNode = newGenericNode({
        address: source,
        interested: interestedAddresses
          .filter((ia) => chain === ia.chain)
          .includes(extractAddress(source)),
        chain,
      });

      newNodesArr.push(newNode);
      newAddressMap.set(extractAddress(source), newNode.id);
    }

    if (!addressInGraph(target, chain)) {
      const newNode = newGenericNode({
        address: target,
        interested: interestedAddresses
          .filter((ia) => chain === ia.chain)
          .includes(extractAddress(target)),
        chain,
      });

      newNodesArr.push(newNode);
      newAddressMap.set(extractAddress(target), newNode.id);
    }

    const focusNode = prepareFocusNode({
      addressMap: newAddressMap,
      filterAddress: focusAddress,
      chain,
      nodes: newNodesArr,
      searchParams: [
        {
          chain,
          filterAddress: focusAddress,
          tokenAddress: token,
          startTime,
          endTime,
        },
      ],
    });

    const queryId = newQueryId({
      tokenAddress: token,
      filterAddress: focusAddress,
      chain,
      start: startTime,
      end: endTime,
    });

    const edgeId = tokenFlowEdgeId({
      source: addressNodeId({ chain, address: source }),
      target: addressNodeId({ chain, address: target }),
    });

    const record = {
      amount: value,
      tokenAddress: token,
      sourceAddress: source,
      targetAddress: target,
      filterAddress: focusAddress,
      chain,
      startTime,
      endTime,
    };

    const tempEdge = newEdgesArr.find((edge) => {
      return edge.id === edgeId;
    });

    if (tempEdge && tempEdge.type === 'genericEdge') {
      tempEdge.data.queries[queryId] = record;
    } else {
      newEdgesArr.push(
        newGenericEdge({
          source: addressNodeId({ chain, address: source }),
          target: addressNodeId({ chain, address: target }),

          queries: { [queryId]: record },
        })
      );
    }

    // Re calculate the layout
    const dagrifiedGraph = dagrify(newNodesArr, newEdgesArr);

    set({
      edges: colorAttachedEdges(
        newEdgesArr,
        addressNodeId({ chain, address: focusAddress })
      ),
      nodes: dagrifiedGraph.nodes,
      addressMap: {
        ...addressMap,
        [chain]: newAddressMap,
      },
      selectedNodeData: {
        ...focusNode.data,
      },
      nodeIdToZoom: focusNode.id,
      unsavedChanges: true,
    });

    updateTracer_Mutation(
      getSaveableTracerObject(
        dagrifiedGraph.nodes,
        newEdgesArr,
        interestedAddresses,
        {
          autoLayout,
          showUniqueTokens,
          showTimeRange,
          showEdgeValue,
          showHoldingValue,
          showChain,
          showMiniMap,
          showAddressRisk,
        },
        currentTracerName,
        currentTracerId
      )
    );
  },

  removeNode(nodeId) {
    const { nodes, edges, addressMap } = get();
    const newAddressMap = {};

    const node = nodes.find((n) => n.id === nodeId);

    if (!node) {
      return;
    }

    if (node.type === 'addressGroup') {
      node.data.members.forEach((member) => {
        const { chain, address } = member.data;
        if (newAddressMap[chain] === undefined) {
          newAddressMap[chain] = new Map(addressMap[chain]);
        }

        newAddressMap[chain].delete(address);
      });
    } else if (node.type === 'addressNode') {
      const { chain, address } = node.data;
      if (newAddressMap[chain] === undefined) {
        newAddressMap[chain] = new Map(addressMap[chain]);
      }
      newAddressMap[chain].delete(address);
    }

    // Setting the new state.
    set({
      nodes: nodes.filter((node) => nodeId !== node.id),
      edges: edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      ),
      addressMap: {
        ...addressMap,
        ...newAddressMap,
      },
      unsavedChanges: true,
    });
  },

  removeQueryFromEdge(edgeId, queryKey, removeFloatingNodes = false) {
    const { edges, nodes, addressMap } = get();

    const { newNodes, newEdges, newAddressMap } = removeQueryFromEdge({
      edgeId,
      queryKey,
      removeFloatingNodes,
      edges,
      nodes,
      addressMap,
    });

    set({
      nodes: newNodes,
      edges: newEdges,
      addressMap: {
        ...newAddressMap,
      },
      unsavedChanges: true,
    });
  },

  onNodeClick(node) {
    if (node.type === 'addressNode' || node.type === 'addressGroup') {
      set({
        edges: colorAttachedEdges(get().edges, node.id),
        selectedNodeData: node.data,
      });
    }
  },

  clearTracer() {
    set({
      nodes: [],
      edges: [],
      groups: [],
      selectedNodeData: {},
      nodeIdToZoom: null,
      addressMap: { ...initState.addressMap },
      rawQueryData: [],
      unsavedChanges: false,
      currentTracerId: null,
      currentTracerName: null,
    });
  },

  updateInterestedNodes() {
    set({
      nodes: get().nodes.map((node) => {
        return {
          ...node,
          data: {
            ...node.data,
            interested:
              node.type === 'addressNode' && node.data.address
                ? get()
                    .interestedAddresses.filter(
                      (ia) => node.data.chain === ia.chain
                    )
                    .includes(extractAddress(node.data.address))
                : false,
          },
        };
      }),
    });
  },

  resetSpacing() {
    const dagrifiedGraph = dagrify(get().nodes, get().edges, true);
    set({
      nodes: dagrifiedGraph.nodes,
      edges: dagrifiedGraph.edges,
    });
  },

  loadSavedTracer(savedNodes, savedEdges, savedSettings) {
    const newNodes = [];
    const newGroups = [];

    /** @type {Record<string,Map<string,string>>} */
    const newAddressMap = {};

    savedNodes.forEach((n) => {
      if (n.type === 'addressNode' && !newAddressMap[n.data.chain]) {
        newAddressMap[n.data.chain] = new Map();
      }

      if (n.type === 'addressGroup') {
        const newGroup = newGroupNode(
          n.data.name,
          n.data.members.map((m) => ({ ...m, id: m.id || m.nodeId })),
          n.data.internalEdges?.map((e) =>
            newGenericEdge({
              ...e.data,
              source: e.source,
              target: e.target,
            })
          ),
          n.savedPosition
        );

        newNodes.push(newGroup);
        newGroups.push(newGroup);
        for (const member of n.data.members) {
          if (!newAddressMap[member.data.chain]) {
            newAddressMap[member.data.chain] = new Map();
          }
          newAddressMap[member.data.chain].set(
            extractAddress(member.data.address),
            n.nodeId
          );
        }
      } else if (n.type === 'addressNode') {
        newNodes.push(newGenericNode(n.data, n.savedPosition));
        newAddressMap[n.data.chain].set(
          extractAddress(n.data.address),
          n.nodeId
        );
      } else if (n.type === 'textNode') {
        newNodes.push(
          newTextNode(n.data.text, n.savedPosition, n.savedSize, n.nodeId)
        );
      }
    });

    const newEdges = savedEdges.map((e) =>
      e.type === 'textEdge'
        ? newTextEdge(e)
        : colorSingleEdge(
            e.type === 'groupEdge'
              ? newGroupEdge({
                  source: e.source,
                  target: e.target,
                  members: e.data.members,
                })
              : newGenericEdge({
                  ...e.data,
                  source: e.source,
                  target: e.target,
                })
          )
    );

    const dagrified = dagrify(newNodes, newEdges);
    set({
      nodes: dagrified.nodes,
      edges: dagrified.edges,
      addressMap: newAddressMap,
      groups: newGroups,
      selectedNodeData: {},
      nodeIdToZoom: null,
      unsavedChanges: false,
      ...savedSettings,
    });
  },

  setUnsavedChanges(unsavedChanges) {
    set({ unsavedChanges });
  },

  setInterestedAddresses(interestedAddresses) {
    set({ interestedAddresses });
  },

  addToGroup({ nodeIds, group }) {
    const { groups, nodes, edges, addressMap } = get();

    const newGroup = prepareGroups({ groups, groupId: group });

    // if using our groupId format
    const groupsList = group.includes('group:::')
      ? [...groups]
      : [newGroup, ...groups];

    const { newNodes, newAddressMap } = computeNodesWithNewGroup({
      nodes,
      nodeIdsToAdd: nodeIds,
      newGroup,
      addressMap,
    });

    const newEdges = computeEdgesWithNewGroup({
      edges,
      nodeIds,
      newGroup,
    });
    // Set the new state
    set({
      groups: groupsList,
      nodes: newNodes,
      edges: colorAttachedEdges(newEdges, newGroup.id),
      addressMap: {
        ...addressMap,
        ...newAddressMap,
      },
      unsavedChanges: true,
    });
  },

  ungroupNodes(groupId) {
    const { groups, nodes, edges, addressMap } = get();

    const {
      newNodes,
      newAddressMap,
      newGroups,
      newEdges: newEdgesAfterNodeUpdate,
    } = computeNodesAfterUngroup({
      nodes,
      groups,
      groupId,
      addressMap,
    });

    const newEdges = computeEdgesAfterUngroup({
      edges,
      newEdgesAfterNodeUpdate,
      newAddressMap,
      groupId,
    });

    // Recalculate the layout
    const dagrifiedGraph = dagrify(newNodes, newEdges);

    // Set the new state
    set({
      nodes: dagrifiedGraph.nodes,
      groups: newGroups,
      edges: colorAttachedEdges(dagrifiedGraph.edges),
      selectedNodeData: {},
      nodeIdToZoom: null,
      addressMap: newAddressMap,
      unsavedChanges: true,
    });
  },

  populateTokenFlowQueryData(data, search) {
    const { rawQueryData } = get();

    const queriesAlreadyInData = new Set();
    for (const row of rawQueryData) {
      queriesAlreadyInData.add(
        newQueryId({
          filterAddress: row.filterAddress,
          tokenAddress: row.tokenAddress,
          start: row.startTime,
          end: row.endTime,
          chain: row.chain,
        })
      );
    }

    if (
      !queriesAlreadyInData.has(
        newQueryId({
          ...search,
          start: search.startTime,
          end: search.endTime,
        })
      )
    ) {
      const newQueryData = [...rawQueryData];

      const { results, params } = data;

      for (let i = 0; i < results.length; i++) {
        const element = results[i];
        const isInflow = element.flow.displayText.charAt(0) !== '-';

        // merge results and search params into queryData
        const record = {
          amount: element.flow,
          ...params,
          sourceAddress: isInflow
            ? element.walletAddress
            : params.filterAddress,
          targetAddress: isInflow
            ? params.filterAddress
            : element.walletAddress,
        };
        newQueryData.push(record);
      }

      set({ rawQueryData: newQueryData });
    }
  },

  populateTransactionFlowQueryData(data, search) {
    const { rawQueryData } = get();

    const queriesAlreadyInData = new Set();
    for (const row of rawQueryData) {
      queriesAlreadyInData.add(
        newQueryId({
          transactionHash: row.transactionHash,
          tokenAddress: row.tokenAddress,
          chain: row.chain,
        })
      );
    }

    if (!queriesAlreadyInData.has(newQueryId(search))) {
      const newQueryData = [...rawQueryData];

      const { results, params } = data;
      const { tokenFlow, txnTime } = results;

      for (let i = 0; i < tokenFlow.length; i++) {
        const element = tokenFlow[i];

        // merge results and search params into queryData
        const record = {
          ...params,
          ...element,
          amount: element.flow,
          txnTime,
        };
        newQueryData.push(record);
      }

      set({ rawQueryData: newQueryData });
    }
  },

  removeQueryFromNode(nodeId, queryKey, removeFloatingNodes = false) {
    const { edges, nodes, addressMap } = get();

    const { newNodes, newEdges, newAddressMap, newSelectedNodeData } =
      removeQueryFromNode({
        nodeId,
        queryKey,
        removeFloatingNodes,
        edges,
        nodes,
        addressMap,
      });

    set({
      nodes: newNodes,
      edges: newEdges,
      addressMap: {
        ...newAddressMap,
      },
      selectedNodeData: { ...newSelectedNodeData },
      unsavedChanges: true,
    });
  },

  addTextNode(text, position) {
    const {
      nodes,
      edges,
      interestedAddresses,

      currentTracerId,
      currentTracerName,
      autoLayout,
      showUniqueTokens,
      showTimeRange,
      showEdgeValue,
      showHoldingValue,
      showChain,
      showMiniMap,
      showAddressRisk,
    } = get();
    const newNode = newTextNode(text, position);
    const newNodesArr = [...nodes, newNode];

    set({
      nodes: newNodesArr,
    });

    if (currentTracerId && currentTracerName) {
      updateTracer_Mutation(
        getSaveableTracerObject(
          newNodesArr,
          edges,
          interestedAddresses,
          {
            autoLayout,
            showUniqueTokens,
            showTimeRange,
            showEdgeValue,
            showHoldingValue,
            showChain,
            showMiniMap,
            showAddressRisk,
          },
          currentTracerName,
          currentTracerId
        )
      );
    }
  },
  updateTextNode(nodeId, text) {
    const {
      nodes,
      edges,
      interestedAddresses,

      currentTracerId,
      currentTracerName,
      autoLayout,
      showUniqueTokens,
      showTimeRange,
      showEdgeValue,
      showHoldingValue,
      showChain,
      showMiniMap,
      showAddressRisk,
    } = get();
    const updatedNodes = nodes.map((node) => {
      if (node.id === nodeId && node.type === 'textNode') {
        return { ...node, data: { ...node.data, text } };
      }
      return node;
    });
    set({
      nodes: updatedNodes,
    });
    if (currentTracerId && currentTracerName) {
      updateTracer_Mutation(
        getSaveableTracerObject(
          updatedNodes,
          edges,
          interestedAddresses,
          {
            autoLayout,
            showUniqueTokens,
            showTimeRange,
            showEdgeValue,
            showHoldingValue,
            showChain,
            showMiniMap,
            showAddressRisk,
          },
          currentTracerName,
          currentTracerId
        )
      );
    }
  },
}));
